import { TravelerTags } from '@marageti/z4-sdk/lib/people';
import { TripRequest, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import pecvCountryReference from './pecv-country-reference.json';
import {
  PecvCountryReference,
  PecvDecisionContributor,
  PecvInferenceResult,
  PecvResultData,
} from './pecv-types';

const defaultPecvCountryReference: PecvCountryReference = {
  continent: 'Unknown',
  popularity: 0,
  is_top_10: false,
  min_pppd: 250,
  min_nights: 5,
};

const pecvCountryReferences: Record<string, PecvCountryReference> = pecvCountryReference;
const pecvPopularityRankByValue: Record<number, number> = {};
Object.values(pecvCountryReferences)
  .sort((a, b) => b.popularity - a.popularity)
  .forEach((country, index) => {
    if (pecvPopularityRankByValue[country.popularity] == null) {
      pecvPopularityRankByValue[country.popularity] = index + 1;
    }
  });
const pecvCountryReferenceCount = Object.keys(pecvCountryReferences).length;

const pecvFeatureLabels: Record<string, string> = {
  Pppd: 'Budget per traveler per day',
  pppd_gap: 'Budget compared with destination norms',
  is_lux_pppd: 'Luxury-level daily budget',
  'Total Budget': 'Total trip budget',
  'Total Nights': 'Trip length',
  nights_gap: 'Trip length compared with destination norms',
  is_lux_nights: 'Luxury-style trip length',
  continent_enc: 'Destination region',
  is_top_10: 'Popular destination',
  popularity: 'Destination popularity',
  'Days to departure': 'Time until departure',
  account_age_days: 'Traveler account age',
  'Is Repeat Traveler': 'Repeat traveler',
  'Is Vip': 'VIP traveler',
  'Is referred': 'Referred traveler',
  'Is Bad Apple': 'Traveler history risk flag',
  'Has Phone': 'Phone number provided',
  'Is Junk': 'Junk request signal',
  'Is Consultation': 'Consultation request',
  name_in_email: 'Name matches email',
  'Num Adults': 'Number of adults',
  'Num Children': 'Number of children',
  'Description Length': 'Trip description detail',
};

const pecvBooleanFeatures = new Set([
  'is_lux_pppd',
  'is_lux_nights',
  'is_top_10',
  'Is Repeat Traveler',
  'Is Vip',
  'Is referred',
  'Is Bad Apple',
  'Has Phone',
  'Is Junk',
  'Is Consultation',
  'name_in_email',
]);

export const formatPecvCurrency = (value: number | undefined) => (
  value == null ? '-' : `$${Math.round(value).toLocaleString()}`
);

export const formatPecvPercent = (value: number | undefined) => (
  value == null ? '-' : `${Math.round(value * 100)}%`
);

export const getPecvFeatureLabel = (feature: string) => {
  if (pecvFeatureLabels[feature]) return pecvFeatureLabels[feature];
  if (/^age_/.test(feature)) return `Traveler age: ${feature.replace('age_', '')}`;
  return feature
    .replace(/_/g, ' ')
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export const formatPecvContributorValue = (feature: string, value: string | number | boolean | null | undefined): string | null => {
  if (value == null || value === '') return null;
  if (feature === 'is_lux_pppd' && typeof value === 'number') return formatPecvCurrency(value);
  if (feature === 'is_lux_nights' && typeof value === 'number') return `${Math.round(value).toLocaleString()} nights`;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if ((pecvBooleanFeatures.has(feature) || /^age_/.test(feature)) && typeof value === 'number') {
    return value >= 1 ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    if (feature === 'popularity') {
      const rank = pecvPopularityRankByValue[value];
      return rank ? `${rank}/${pecvCountryReferenceCount}` : null;
    }
    if (feature === 'Total Budget' || feature === 'Pppd' || feature === 'pppd_gap') {
      return formatPecvCurrency(value);
    }
    if (feature === 'Total Nights' || feature === 'nights_gap') {
      return `${Math.round(value).toLocaleString()} nights`;
    }
    if (feature === 'Days to departure' || feature === 'account_age_days') {
      return `${Math.round(value).toLocaleString()} days`;
    }
    if (feature === 'Num Adults' || feature === 'Num Children') {
      return Math.round(value).toLocaleString();
    }
    if (feature === 'Description Length') {
      return `${Math.round(value).toLocaleString()} characters`;
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  return value;
};

export const getRankedPecvContributors = (contributors: PecvDecisionContributor[], direction: 'Positive' | 'Negative') => (
  contributors
    .filter((contributor) => contributor.direction.toLowerCase() === direction.toLowerCase())
    .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))
);

const hasTravelerTag = (tripRequest: TripRequest, tag: TravelerTags) => (
  tripRequest.travelers?.[0]?.tags?.includes(tag) ?? false
);

const getAccountAgeDays = (tripRequest: TripRequest) => {
  const travelerCreatedAt = tripRequest.travelers?.[0]?.createdAt;
  if (!tripRequest.createdAt || !travelerCreatedAt) return null;

  const ms = new Date(tripRequest.createdAt).getTime() - new Date(travelerCreatedAt).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(Math.round(ms / (1000 * 60 * 60 * 24)), 0);
};

const getDaysToDeparture = (tripRequest: TripRequest) => {
  if (!tripRequest.departureDate || !tripRequest.createdAt) return null;

  const ms = new Date(tripRequest.departureDate).getTime() - new Date(tripRequest.createdAt).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(Math.round(ms / (1000 * 60 * 60 * 24)), 0);
};

const getNameInEmail = (tripRequest: TripRequest) => {
  const traveler = tripRequest.travelers?.[0];
  if (!traveler?.email) return null;

  const email = traveler.email.toLowerCase();
  const names = [traveler.givenName, traveler.familyName]
    .filter((name) => name && name.length > 2)
    .map((name) => name.toLowerCase());

  if (names.length === 0) return null;
  return names.some((name) => email.includes(name));
};

const getPecvCountryReference = (tripRequest: TripRequest) => {
  const countryCode = tripRequest.countries?.[0];
  if (!countryCode) return null;
  return pecvCountryReferences[countryCode] || defaultPecvCountryReference;
};

export const getTripRequestContributorValue = (
  feature: string,
  tripRequest: TripRequest | undefined,
): string | number | boolean | null => {
  if (!tripRequest) return null;
  const countryReference = getPecvCountryReference(tripRequest);

  if (/^age_/.test(feature)) {
    const ageGroup = feature.replace('age_', '');
    return [
      ...(tripRequest.adultAgeGroups || []),
      ...(tripRequest.childAgeGroups || []),
    ].includes(ageGroup);
  }

  switch (feature) {
    case 'Total Budget':
      return tripRequest.totalBudget ?? null;
    case 'Total Nights':
      return tripRequest.totalNights ?? null;
    case 'nights_gap':
      return countryReference ? tripRequest.totalNights - countryReference.min_nights : null;
    case 'is_lux_nights':
      return countryReference ? tripRequest.totalNights >= countryReference.min_nights : null;
    case 'Pppd':
      return tripRequest.pppd ?? null;
    case 'pppd_gap':
      return countryReference ? tripRequest.pppd - countryReference.min_pppd : null;
    case 'is_lux_pppd':
      return countryReference ? tripRequest.pppd >= countryReference.min_pppd : null;
    case 'is_top_10':
      return countryReference?.is_top_10 ?? null;
    case 'popularity':
      return countryReference?.popularity ?? null;
    case 'Num Adults':
      return tripRequest.numberOfAdults ?? null;
    case 'Num Children':
      return tripRequest.numberOfChildren ?? null;
    case 'Description Length':
      return tripRequest.tripDescription?.length ?? null;
    case 'account_age_days':
      return getAccountAgeDays(tripRequest);
    case 'Is Repeat Traveler':
      return tripRequest.repeatTraveler ?? null;
    case 'Is Vip':
      return hasTravelerTag(tripRequest, TravelerTags.VIP);
    case 'Is Bad Apple':
      return hasTravelerTag(tripRequest, TravelerTags.BAD_APPLE);
    case 'Has Phone':
      return Boolean(tripRequest.travelers?.[0]?.phone?.number);
    case 'Is Junk':
      return tripRequest.status === TripRequestStatus.JunkStatus;
    case 'Is Consultation':
      return tripRequest.consultation ?? null;
    case 'Is referred':
      return Boolean(tripRequest.referralType || tripRequest.referralId);
    case 'name_in_email':
      return getNameInEmail(tripRequest);
    case 'continent_enc':
      return countryReference?.continent ?? null;
    case 'Days to departure':
      return getDaysToDeparture(tripRequest);
    default:
      return null;
  }
};

export const getPecvResultData = (result: PecvInferenceResult | null): PecvResultData => {
  if (!result?.results) return {};
  if (typeof result.results !== 'string') return result.results;

  try {
    return JSON.parse(result.results) as PecvResultData;
  } catch {
    return {};
  }
};
